import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  BeakerIcon,
  ChartBarIcon,
  CloudArrowDownIcon,
  CodeBracketIcon,
  MagnifyingGlassCircleIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import {
  NAV_TOOLS_MENU,
  PRODUCT_DEMO_HREF,
  TOOLS_OVERVIEW_HREF,
  toolShowsInfoLink,
  isNavToolVisible,
  type NavToolMenuItem,
} from './navMenuConfig';
import { userAppContext } from '@/context/Context';
import { slugifyTrackingValue, trackEvent } from '@/utils/analytics';

type ToolsMegaMenuProps = {
  onNavigate: () => void;
};

function trackToolsClick(
  label: string,
  href: string,
  kind: 'info' | 'tool' | 'overview' | 'demo',
  section = 'top_navigation_mega_tools',
) {
  trackEvent({
    name: 'navigation_click',
    buttonId: `navigation_tools_${kind}_${slugifyTrackingValue(href)}`,
    label: `${label} — ${kind}`,
    page: 'global',
    section,
    feature: 'navigation',
    eventCategory: 'navigation',
  });
}

function ToolCardIcon({ tool }: { tool: NavToolMenuItem }) {
  if (tool.iconSrc) {
    const style = tool.iconWidth ? { width: `${tool.iconWidth}%`, height: `${tool.iconWidth}%` } : undefined;
    return <img src={tool.iconSrc} alt="" className="nav-mega-tool-card__icon-img" style={style} />;
  }

  const className = 'nav-mega-tool-card__icon-svg';
  switch (tool.id) {
    case 'geo-browse':
      return <BeakerIcon className={className} aria-hidden />;
    case 'advanced-search':
      return <MagnifyingGlassCircleIcon className={className} aria-hidden />;
    case 'enrichment':
      return <ChartBarIcon className={className} aria-hidden />;
    case 'download':
      return <CloudArrowDownIcon className={className} aria-hidden />;
    case 'ontology-explorer':
      return <ShareIcon className={className} aria-hidden />;
    default:
      return <CodeBracketIcon className={className} aria-hidden />;
  }
}

export function NavToolCard({
  tool,
  onNavigate,
  trackingSection = 'top_navigation_mega_tools',
}: {
  tool: NavToolMenuItem;
  onNavigate: () => void;
  trackingSection?: string;
}) {
  const showInfo = toolShowsInfoLink(tool);
  const showTool = tool.showTool !== false;
  const openToolLink = (
    <span className="nav-mega-tool-card__action nav-mega-tool-card__action--primary">
      Open tool
      <ArrowRightIcon className="nav-mega-tool-card__arrow" aria-hidden />
    </span>
  );

  return (
    <article className="nav-mega-tool-card">
      <div className="nav-mega-tool-card__icon-wrap" aria-hidden>
        <ToolCardIcon tool={tool} />
      </div>
      <div className="nav-mega-tool-card__body">
        <h3 className="nav-mega-tool-card__title">{tool.label}</h3>
        <p className="nav-mega-tool-card__description">{tool.description}</p>
        <div
          className={`nav-mega-tool-card__actions${
            showInfo && showTool ? '' : ' nav-mega-tool-card__actions--tool-only'
          }`}
        >
          {showInfo && (
            <Link
              to={tool.infoHref}
              className="nav-mega-tool-card__action nav-mega-tool-card__action--muted"
              onClick={() => {
                trackToolsClick(tool.label, tool.infoHref, 'info', trackingSection);
                onNavigate();
              }}
            >
              Info
            </Link>
          )}
          {showTool &&
            (tool.toolExternal ? (
              <a
                href={tool.toolHref}
                className="nav-mega-tool-card__action nav-mega-tool-card__action--primary"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackToolsClick(tool.label, tool.toolHref, 'tool', trackingSection);
                  onNavigate();
                }}
              >
                {openToolLink}
              </a>
            ) : (
              <Link
                to={tool.toolHref}
                className="nav-mega-tool-card__action nav-mega-tool-card__action--primary"
                onClick={() => {
                  trackToolsClick(tool.label, tool.toolHref, 'tool', trackingSection);
                  onNavigate();
                }}
              >
                {openToolLink}
              </Link>
            ))}
        </div>
      </div>
    </article>
  );
}

export default function ToolsMegaMenu({ onNavigate }: ToolsMegaMenuProps) {
  const { user } = userAppContext();
  const userRoles: string[] = user?.['cognito:groups'] ?? [];

  const visibleTools = NAV_TOOLS_MENU.filter(tool => isNavToolVisible(tool, userRoles));

  return (
    <div className="nav-mega-tools-panel">
      <header className="nav-mega-tools-panel__header">
        <div>
          <p className="nav-mega-section-title">TOOLS</p>
          <p className="nav-mega-tools-panel__subtitle">Explore the DISGENET ecosystem</p>
        </div>
        <Link
          to={TOOLS_OVERVIEW_HREF}
          className="nav-mega-tools-panel__overview"
          onClick={() => {
            trackToolsClick('View all tools', TOOLS_OVERVIEW_HREF, 'overview');
            onNavigate();
          }}
        >
          View all tools
          <ArrowRightIcon className="nav-mega-tools-panel__overview-arrow" aria-hidden />
        </Link>
      </header>

      <div className="nav-mega-tools-panel__grid">
        {visibleTools.map(tool => (
          <NavToolCard key={tool.id} tool={tool} onNavigate={onNavigate} />
        ))}
      </div>

      <footer className="nav-mega-tools-panel__footer">
        <Link
          to={PRODUCT_DEMO_HREF}
          className="nav-mega-tools-panel__demo-link"
          onClick={() => {
            trackToolsClick('Interactive Demo', PRODUCT_DEMO_HREF, 'demo');
            onNavigate();
          }}
        >
          Interactive Demo
          <ArrowRightIcon className="nav-mega-tools-panel__demo-arrow" aria-hidden />
        </Link>
      </footer>
    </div>
  );
}
